import React, { FC, memo, PropsWithChildren, useEffect } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';
import { initializeTrainerPresence } from 'src/services/presence/trainerPresence';

import WaitlistModal from 'src/components/Modals/WaitlistModal/WaitlistModal.tsx';
import QuizModal from 'src/components/Modals/QuizModal/QuizModal.tsx';
import VideoOverlay from 'src/components/VideoOverlay/VideoOverlay.tsx';

import toast from 'react-hot-toast';
import { onForegroundFCMMessage, printCurrentFCMToken } from 'src/services/push/registerPush';
import { uiStore } from 'src/store/uiStore.ts';
import { useScrollSmootherLock } from 'src/hooks/useBodyScrollLock.ts';

const AppManager: FC<PropsWithChildren> = ({ children }) => {
  useEffect(() => {
    return onIdTokenChanged(auth, async (user) => {
      if (!user) {
        // Firebase says user is logged out
        sessionStore.getState().logout();
      } else {
        try {
          // Keep the ID token fresh in Zustand store even when reopening the browser next day
          const freshToken = await user.getIdToken();
          const currentStore = sessionStore.getState();
          if (currentStore.token !== freshToken || !currentStore.isLoggedIn) {
            currentStore.setToken(freshToken, currentStore.currentUser || undefined);
          }
          printCurrentFCMToken();
        } catch (err) {
          console.warn('Failed to refresh Firebase token:', err);
        }
      }
    });
  }, []);

  // Automatic presence tracking for trainer (is_online, last_seen)
  useEffect(() => {
    let presenceCleanup: (() => void) | null = null;

    const unsubscribe = onIdTokenChanged(auth, (user) => {
      if (presenceCleanup) {
        presenceCleanup();
        presenceCleanup = null;
      }

      if (user && user.uid) {
        presenceCleanup = initializeTrainerPresence(user.uid);
      }
    });

    return () => {
      if (presenceCleanup) {
        presenceCleanup();
      }
      unsubscribe();
    };
  }, []);

  // Foreground FCM Push Notification listener
  useEffect(() => {
    return onForegroundFCMMessage((payload) => {
      const title = payload.notification?.title || payload.data?.title || 'miniPT';
      let body = payload.notification?.body || payload.data?.body || 'You received a new message.';
      let clickUrl = '/onboarding-trainer/dashboard/messages';

      if (payload.data?.content) {
        try {
          const content =
            typeof payload.data.content === 'string'
              ? JSON.parse(payload.data.content)
              : payload.data.content;
          if (content.body) body = content.body;
          if (content.chat_id) {
            clickUrl = `/onboarding-trainer/dashboard/messages/${content.chat_id}`;
            if (content.message_type === 'inquiry' && content.message_id) {
              clickUrl += `?inquiryId=${content.message_id}`;
            }
          }
          if (content.message_type === 'checkin' && content.checkin_id) {
            clickUrl = `/onboarding-trainer/dashboard/messages/checkin/${content.checkin_id}`;
          }
          if (content.click_url) clickUrl = content.click_url;
        } catch {
          // Ignored
        }
      } else if (payload.data?.click_url) {
        clickUrl = payload.data.click_url;
      }

      toast(
        (t) => (
          <div
            style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '2px' }}
            onClick={() => {
              toast.dismiss(t.id);
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('app:navigate', { detail: clickUrl }));
              }
            }}
          >
            <strong style={{ color: '#fff', fontSize: '13px' }}>{title}</strong>
            <span style={{ color: '#F59E0B', fontSize: '12px' }}>{body}</span>
          </div>
        ),
        {
          icon: '💬',
          duration: 6000,
          style: {
            background: '#1A1A1A',
            border: '1px solid #333333',
            color: '#FFFFFF',
            borderRadius: '8px',
          },
        },
      );
    });
  }, []);
  //Waitlist model
  const waitlistModalVisible = uiStore((s) => s.waitlistModalVisible);
  const setWaitlistModalVisible = uiStore((s) => s.setWaitlistModalVisible);
  const waitlistEmail = uiStore((s) => s.waitlistEmail);
  const quizModalVisible = uiStore((s) => s.quizModalVisible);
  const setQuizModalVisible = uiStore((s) => s.setQuizModalVisible);

  //Video player link
  const hideVideoPlayerVisible = uiStore((s) => s.hideVideoPlayerVisible);
  const videoPlayerLink = uiStore((s) => s.videoPlayerLink);
  const videoPlayerVisible = uiStore((s) => s.videoPlayerVisible);

  useScrollSmootherLock(waitlistModalVisible);

  return (
    <>
      {children}
      <WaitlistModal
        visible={waitlistModalVisible}
        onClose={() => setWaitlistModalVisible(false)}
        initialEmail={waitlistEmail}
      />
      <QuizModal visible={quizModalVisible} onClose={() => setQuizModalVisible(false)} />
      <VideoOverlay
        video={videoPlayerLink!}
        open={videoPlayerVisible}
        onClose={() => hideVideoPlayerVisible()}
      />
    </>
  );
};

export default memo(AppManager);
